import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import FilterSidebar, { type FilterState } from './components/FilterSidebar';
import MovieCard from '@/components/base/MovieCard';
import StateBlock from '@/components/base/StateBlock';
import { categoryInfo, mediaList, sortOptions, type MediaType } from '@/mocks/media';
import { useSeo } from '@/hooks/useSeo';

const PAGE_SIZE = 8;
const validTypes: MediaType[] = ['movie', 'tv', 'variety'];

const emptyFilter: FilterState = { genres: [], region: '', year: '' };

export default function Category() {
  const { type } = useParams<{ type: string }>();
  const mediaType = (validTypes.includes(type as MediaType) ? type : 'movie') as MediaType;
  const info = categoryInfo[mediaType];

  const [filter, setFilter] = useState<FilterState>(emptyFilter);
  const [sort, setSort] = useState('updated');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useSeo({
    title: `${info.name} - 看片影视大全`,
    description: `看片影视${info.name}，${info.description}${info.slogan}。`,
    keywords: `看片,${info.name},${info.slogan},影视大全`,
  });

  useEffect(() => {
    setFilter(emptyFilter);
    setPage(1);
  }, [mediaType]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [mediaType, filter, sort, page]);

  const filtered = useMemo(() => {
    const list = mediaList.filter((item) => {
      if (item.type !== mediaType) return false;
      if (filter.genres.length > 0 && !filter.genres.some((g) => item.genres.includes(g))) return false;
      if (filter.region && item.region !== filter.region) return false;
      if (filter.year && String(item.year) !== filter.year) return false;
      return true;
    });
    const sorted = [...list];
    if (sort === 'heat') sorted.sort((a, b) => b.heat - a.heat);
    else if (sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    return sorted;
  }, [mediaType, filter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const hasActiveFilter = filter.genres.length > 0 || filter.region !== '' || filter.year !== '';

  const resetAll = () => {
    setFilter(emptyFilter);
    setPage(1);
  };

  const sidebar = (
    <FilterSidebar
      filter={filter}
      onChange={(next) => {
        setFilter(next);
        setPage(1);
      }}
      onReset={resetAll}
    />
  );

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-900 font-medium">{info.name}</span>
      </nav>

      <div className="rounded-lg bg-primary-950 text-background-50 px-6 md:px-8 py-7 mb-8">
        <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight mb-2">{info.name}</h1>
        <p className="text-sm text-background-100/80">{info.slogan} · {info.description}</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-20 rounded-lg bg-background-50 border border-background-200/70 p-5">
            {sidebar}
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMobileFilterOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-background-100 border border-background-200 text-sm font-medium text-foreground-700 cursor-pointer hover:border-primary-300 transition-colors whitespace-nowrap"
              >
                <i className="ri-filter-3-line"></i>
                筛选
              </button>
              <p className="text-sm text-foreground-500">
                共 <span className="font-semibold text-primary-600">{filtered.length}</span> 部作品
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-500 whitespace-nowrap">排序：</span>
              <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      setSort(option.key);
                      setPage(1);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-colors ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                        : 'text-foreground-600 hover:text-primary-600'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
              {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="w-full aspect-[2/3] rounded-lg bg-background-200"></div>
                  <div className="mt-2.5 h-4 w-3/4 rounded bg-background-200"></div>
                  <div className="mt-2 h-3 w-1/2 rounded bg-background-200"></div>
                </div>
              ))}
            </div>
          ) : paged.length === 0 ? (
            <StateBlock
              icon="ri-emotion-sad-line"
              title="未找到符合条件的影视作品"
              description="试试调整筛选条件，或重置全部筛选后重新浏览。"
              actionText="重置筛选"
              onAction={resetAll}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {paged.map((item) => (
                  <MovieCard key={item.id} item={item} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-10">
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer hover:border-primary-300 transition-colors"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium cursor-pointer transition-colors ${
                        currentPage === i + 1
                          ? 'bg-primary-500 text-background-50 dark:text-foreground-950'
                          : 'border border-background-200 text-foreground-600 hover:border-primary-300'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer hover:border-primary-300 transition-colors"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {mobileFilterOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] flex">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setMobileFilterOpen(false)}
          />
          <div className="relative ml-auto w-[85%] max-w-sm h-full bg-background-50 overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-bold text-foreground-950">筛选</h2>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {sidebar}
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-6 w-full py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-semibold cursor-pointer"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}